import React from "react";
import styled from "styled-components";
import { NavLink } from "react-router-dom";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";


const SidebarMenuItem = ({ menu, toggle, isOpen, activeMenu, closeSidebar, isVoucherCustomer }) => {
  const { user } = useAuth();

  // Helper function to check if the user has access to a menu or submenu
  const hasAccess = (itemRoles) =>
    !itemRoles || itemRoles.some((role) => user?.roles?.includes(role));

  // Filter submenus based on user roles and voucher flag when required
  const filteredSubMenus = menu.subMenus?.filter((subMenu) => {
    if (!hasAccess(subMenu.roles)) return false;
    if (subMenu.requiresVoucherCustomer === true && !isVoucherCustomer) return false;
    return true;
  });

  if (!hasAccess(menu.roles)) {
    // If the user has no access to the main menu, return nothing
    return null;
  }

  // Helper for mobile detection
  const isMobile = () => window.innerWidth < 768;
  const handleMenuClick = (e) => {
    if (menu.subMenus) {
      toggle();
    } else {
      if (isMobile() && closeSidebar) closeSidebar();
    }
  };

  const isActive = activeMenu === menu.title;
  return (
    <MenuItemContainer>
      <MenuItem
        to={menu.path || "#"}
        onClick={handleMenuClick}
        className={({ isActive }) => (isActive ? "active" : "")}
        $isActive={isActive}
        end
      >
        <MenuItemContent>
          {menu.iconPath && <MenuIcon src={menu.iconPath} alt={`${menu.title} icon`} />}
          <span>{menu.title}</span>
        </MenuItemContent>
        {menu.subMenus && (
          <CollapseIcon $isOpen={isOpen}>
            {isOpen ? <FaAngleUp /> : <FaAngleDown />}
          </CollapseIcon>
        )}
      </MenuItem>
      {filteredSubMenus && filteredSubMenus.length > 0 && (
        <SubMenuContainer
          style={{
            maxHeight: isOpen ? `${filteredSubMenus.length * 35}px` : "0",
          }}
        >
          {filteredSubMenus.map((subMenu, index) => (
            <SubMenuItem
              key={index}
              to={subMenu.path}
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={() => {
                if (isMobile() && closeSidebar) closeSidebar();
              }}
            >
              {subMenu.title}
            </SubMenuItem>
          ))}
        </SubMenuContainer>
      )}
    </MenuItemContainer>
  );
};

export default SidebarMenuItem;


const MenuItemContainer = styled.div`
  width: 100%;
`;

const MenuItem = styled(NavLink)`
    padding: 12px 20px;
    display: flex;
    align-items: center;
  justify-content: space-between;
    text-decoration: none;
    border-radius: 6px;
    margin-bottom: 3px;
    transition: background-color 0.3s ease;
    background-color: ${(props) => (props.$isActive ? "#f0f4f8" : "transparent")};
    color: #333;

    &.active {
        background-color: #fff; /* Apply active background color */
        color: #141414; /* Active text color */
    }

    &:hover {
        background-color: #e0e7ef;
    }

    @media (max-width: 767px) {
        font-size: 1.14rem;
    }
`;


const SubMenuContainer = styled.div`
  background: #ffffff;
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  margin-bottom: 5px;
`;

const SubMenuItem = styled(NavLink)`
    padding-left: 40px;
    display: block;
    color: #4a4a4a; /* Dark grey text */
    text-decoration: none;
    padding: 8px 20px;
    font-size: 0.9rem;
    transition: background-color 0.3s ease, color 0.3s ease;

    &.active {
        background-color: #f9f9f9; /* Slightly darker grey for active */
        color: #333333; /* Active text color */
    }

    &:hover {
        background-color: #f5f5f5; /* Light grey hover background */
        color: #333333; /* Darker grey hover text */
    }

    @media (max-width: 767px) {
        font-size: 1.05rem;
    }
`;

const MenuIcon = styled.img`
    width: 20px;
    height: 20px;
  flex-shrink: 0;
`;


const CollapseIcon = styled.span`
  color: #ffffff;
  margin-left: auto;
  transition: transform 0.3s;
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

const MenuItemContent = styled.span`
    display: flex;
    align-items: center;
    gap: 12px;
`;
